import { useEffect, useLayoutEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cancelCoachingAIJob, createCoachingAIJob, createCoachingActivity, createCoachingCapture, getCoachingActivities, getCanonicalCoachingFocuses, listCoachFeedback, listFeedbackGoalChoices, getCoachingAIJob, getCoachingAIJobResult, resolveCanonicalCoachingFocus } from '@/lib/api';
import { useScopedKey, invalidateScope } from '@/lib/queryClient';
import { isCurrentDraftContext, isCurrentScopedIdentity, type DraftContext } from '@/features/coach-review/composerDrafts';
import type { CoachingAIJobProgress, CreateCoachingAIJobRequest, CreateCoachingActivityRequest } from '@/lib/types';
import { isRunning } from './assistPresentation';
import { useOwnedAssistState } from './ownedAssistState';
import { captureCoachingManualCommand } from '@/lib/coachingManualCommands';
import type { CoachingManualCommand } from '@/lib/types';
import { captureRecapBoundary } from '@/lib/api';

export type AssistFocusChoice =
  | { kind: 'feedback'; feedbackId: string; revisionId: string; version: number; label: string }
  | { kind: 'goal'; goalId: string; label: string };
export type AssistFocusInput = { source: AssistFocusChoice; playerId: string; activityId: string;
  expectedRevision: string; operationId: string };
type FocusCommand = Extract<CoachingManualCommand, { action: 'select-focus' }>;
type FocusAttempt = { input: AssistFocusInput; command: ReturnType<typeof captureCoachingManualCommand<FocusCommand>> };

/** Read every authorized server page for the existing selector. No local
 * reinterpretation of a partial page as the full choice set. */
export async function readAssistFocusChoices(owner: DraftContext, playerId: string, signal: AbortSignal): Promise<AssistFocusChoice[]> {
  const check = () => { assertAssistOwner(owner); if (signal.aborted) throw new DOMException('Focus read cancelled', 'AbortError'); };
  const feedback: AssistFocusChoice[] = [], goals: AssistFocusChoice[] = [];
  let cursor: string | undefined;
  const seen = new Set<string>();
  do {
    check();
    const page = await listCoachFeedback({ player_id: playerId, publication: 'all', limit: 50, cursor },
      { scope: { ...owner, view: 'coach' }, signal });
    check();
    for (const row of page.items) if (!feedback.some(item => item.kind === 'feedback' && item.feedbackId === row.feedback_id)) {
      feedback.push({ kind: 'feedback', feedbackId: row.feedback_id, revisionId: row.feedback_revision_id,
        version: row.feedback_version, label: row.message_excerpt || 'Feedback' });
    }
    cursor = page.next_cursor ?? undefined;
    if (cursor && seen.has(cursor)) throw new Error('Focus collection requires refresh');
    if (cursor) seen.add(cursor);
  } while (cursor);
  seen.clear();
  do {
    check();
    const page = await listFeedbackGoalChoices({ player_ids: [playerId], limit: 50, cursor }, { scope: owner, signal });
    check();
    for (const row of page.items) goals.push({ kind: 'goal', goalId: row.goal_id, label: row.title });
    cursor = page.next_cursor ?? undefined;
    if (cursor && seen.has(cursor)) throw new Error('Focus collection requires refresh');
    if (cursor) seen.add(cursor);
  } while (cursor);
  return [...feedback, ...goals];
}

export function assertAssistOwner(owner: DraftContext): void {
  if (!isCurrentDraftContext(owner)) throw new Error('Coaching context changed');
}
/** Check at actual dispatch and settlement, including after preceding async work.
 * Old 401s become inert context errors before reaching the global query handler. */
async function guardedRequest<T>(owner:DraftContext,request:()=>Promise<T>,assertOwner:(context:DraftContext)=>void):Promise<T> {
  assertOwner(owner);
  try {const result=await request();assertOwner(owner);return result;}
  catch(error){assertOwner(owner);throw error;}
}
export function ownedRequest<T>(owner:DraftContext,request:()=>Promise<T>):Promise<T> { return guardedRequest(owner,request,assertAssistOwner); }
/** Ordinary viewer metadata uses the same coherent scope/logout generation. */
export function ownedScopedRequest<T>(owner:DraftContext,request:()=>Promise<T>):Promise<T> {
  return guardedRequest(owner,request,context=>{if(!isCurrentScopedIdentity(context))throw new Error('Recording context changed');});
}

export function useAssistContext(owner: DraftContext, playerIds: string[], visible = true, prepareVisible = visible) {
  const client = useQueryClient();
  const scope = { teamId: owner.teamId, seasonId: owner.seasonId, ...(owner.seasonId === null ? { seasonMode: 'unseasoned' as const } : {}) };
  const active = useRef(prepareVisible); active.current = prepareVisible;
  const alive = useRef(true);
  const instance = useRef(crypto.randomUUID());
  const playerKey = JSON.stringify(playerIds);
  const currentPlayers = useRef(playerKey); currentPlayers.current = playerKey;
  const [jobsByPlayers, setJobsByPlayers] = useOwnedAssistState<Record<string, CoachingAIJobProgress>>(owner, 'jobs', {});
  const jobsRef = useRef(jobsByPlayers); jobsRef.current = jobsByPlayers;
  const job = jobsByPlayers[playerKey];
  const setJob = (value: CoachingAIJobProgress | undefined, players = currentPlayers.current, expectedId?: string) => setJobsByPlayers(previous => {
    // Recheck against queued state, not only the last rendered ref: another
    // admission may already have enqueued a replacement in this React batch.
    if (expectedId !== undefined && previous[players]?.id !== expectedId) return previous;
    if (previous[players] === value) return previous;
    const next = { ...previous };
    if (value) next[players] = value; else delete next[players];
    return next;
  });
  const [activityRequest] = useOwnedAssistState(owner, 'activity-request', { current: null as CreateCoachingActivityRequest | null });
  const [admissionRequests] = useOwnedAssistState(owner, 'admission-requests', { current: new Map<string, CreateCoachingAIJobRequest>() });
  const [admissionErrors, setAdmissionErrors] = useOwnedAssistState<Record<string, Error>>(owner, 'admission-errors', {});
  const [activityId, setActivityId] = useOwnedAssistState(owner, 'activity-id', '');
  const activitiesKey = useScopedKey('coach-assist-activities', { author: owner.actorId });
  const focusKey = useScopedKey('coach-assist-canonical-focuses', { activityId, author: owner.actorId, instance: instance.current });
  const choicesKey = useScopedKey('coach-assist-canonical-choices', { players: playerKey, author: owner.actorId, instance: instance.current });
  const [focusAttempt] = useOwnedAssistState(owner, 'canonical-focus-attempt', { current: null as FocusAttempt | null });
  const [focusDispatch] = useOwnedAssistState(owner, 'canonical-focus-dispatch', { current: null as { input: AssistFocusInput; promise: Promise<Awaited<ReturnType<FocusAttempt['command']['send']>>> } | null });
  const focusRead = useRef<{ abort: AbortController; boundary: ReturnType<typeof captureRecapBoundary> } | null>(null);
  const retireFocusRead = () => { focusRead.current?.abort.abort(); focusRead.current?.boundary.dispose(); focusRead.current = null; };
  useLayoutEffect(() => {
    if (!prepareVisible) retireFocusRead();
    return retireFocusRead;
  }, [prepareVisible, owner, playerKey, activityId]);
  const jobKey = useScopedKey('coach-assist-job', { id: job?.id, instance: instance.current });
  const resultKey = useScopedKey('coach-assist-result', { id: job?.id, instance: instance.current });
  const activities = useQuery({ queryKey: activitiesKey, queryFn: ({ signal }) => ownedRequest(owner, () => getCoachingActivities(scope, signal)), enabled: visible, retry: false });
  const focuses = useQuery({ queryKey: focusKey, queryFn: ({ signal }) => ownedRequest(owner, () => getCanonicalCoachingFocuses(activityId, scope, signal)), enabled: prepareVisible && !!activityId, retry: false, refetchOnWindowFocus: prepareVisible });
  const sourceChoices = useQuery({ queryKey: choicesKey,
    queryFn: ({ signal }) => readAssistFocusChoices(owner, playerIds[0], signal),
    enabled: prepareVisible && playerIds.length === 1, retry: false, staleTime: 0 });
  const progress = useQuery({ queryKey: jobKey,
    queryFn: ({ signal }) => ownedRequest(owner, () => getCoachingAIJob(job!.id, scope, signal)),
    enabled: prepareVisible && !!job && isRunning(job), retry: false,
    refetchInterval: query => prepareVisible && !query.state.error && isRunning(query.state.data?.job ?? job) ? 1500 : false,
    refetchOnWindowFocus: false,
  });
  // Retain only owned job metadata across player switches. Private progress/
  // result query payloads still leave the cache on every key change below.
  useEffect(() => {
    const latest = progress.data?.job;
    if (latest && alive.current && isCurrentDraftContext(owner) && jobsRef.current[playerKey]?.id === latest.id) setJob(latest, playerKey, latest.id);
  }, [progress.data, playerKey, owner]);
  const currentJob = progress.data?.job ?? job;
  const result = useQuery({ queryKey: resultKey,
    queryFn: ({ signal }) => ownedRequest(owner, () => getCoachingAIJobResult(job!.id, scope, signal)),
    enabled: prepareVisible && currentJob?.status === 'needs_review' && currentJob.result_available, retry: false,
    staleTime: 0, refetchOnWindowFocus: prepareVisible,
  });
  const operation = useMutation({
    meta: { composerContext: owner }, retry: false,
    mutationFn: (request: CreateCoachingAIJobRequest) => ownedRequest(owner, () => createCoachingAIJob(request, scope)),
    onSuccess: (data, request) => { if (isCurrentDraftContext(owner)) {
      const players = JSON.stringify(request.evidence.player_ids);
      setAdmissionErrors(previous => { const next = { ...previous }; delete next[players]; return next; });
      if (admissionRequests.current.get(players)?.client_operation_id === request.client_operation_id) setJob(data.job, players);
    } },
    onError: (error, request) => { if (isCurrentDraftContext(owner)) {
      const players = JSON.stringify(request.evidence.player_ids);
      setAdmissionErrors(previous => ({ ...previous, [players]: error }));
    } },
  });
  const cancel = useMutation({ meta: { composerContext: owner }, retry: false,
    mutationFn: (input: { id: string; players: string; jobKey: readonly unknown[]; resultKey: readonly unknown[] }) => ownedRequest(owner, () => cancelCoachingAIJob(input.id, scope)),
    onSuccess: async (data, input) => {
      await client.cancelQueries({ queryKey: input.jobKey, exact: true });
      client.removeQueries({ queryKey: input.jobKey, exact: true });
      client.removeQueries({ queryKey: input.resultKey, exact: true });
      if (alive.current && isCurrentDraftContext(owner) && jobsRef.current[input.players]?.id === input.id) setJob(data.job, input.players, input.id);
    },
  });
  const newActivity = useMutation({ meta: { composerContext: owner }, retry: false,
    mutationFn: (request: CreateCoachingActivityRequest) => ownedRequest(owner, () => createCoachingActivity(request, scope)),
    onSuccess: (data, request) => { if (isCurrentDraftContext(owner)) {
      if (activityRequest.current?.client_operation_id === request.client_operation_id) activityRequest.current = null;
      setActivityId(data.activity.id); void activities.refetch();
    } },
  });
  const capture = useMutation({ meta: { composerContext: owner }, retry: false,
    mutationFn: (input: { text: string; activityId: string; playerIds: string[]; occurredAt: string; operationId: string }) => ownedRequest(owner, () => createCoachingCapture({ activity_id: input.activityId, kind: 'text', text: input.text, player_ids: input.playerIds, occurred_at: input.occurredAt, client_operation_id: input.operationId }, scope)),
  });
  const selectFocus = (original: AssistFocusInput) => {
    if (focusDispatch.current) {
      if (JSON.stringify(focusDispatch.current.input) !== JSON.stringify(original)) return Promise.reject(new Error('Resolve the original focus operation first'));
      return focusDispatch.current.promise;
    }
    const input = structuredClone(original);
    const preparedRead = focusAttempt.current ? null
      : { abort: new AbortController(), boundary: captureRecapBoundary({ ...owner, view: 'coach' }) };
    if (preparedRead) focusRead.current = preparedRead;
    const promise = Promise.resolve().then(async () => {
      assertAssistOwner(owner);
      if (!alive.current || !active.current) throw new Error('Focus context hidden');
      if (focusAttempt.current && JSON.stringify(focusAttempt.current.input) !== JSON.stringify(input)) {
        throw new Error('Resolve the original focus operation first');
      }
      if (!focusAttempt.current) {
        const read = preparedRead!;
        if (read.abort.signal.aborted || !read.boundary.current() || focusRead.current !== read)
          throw new Error('Focus context hidden or changed');
        let proof: Awaited<ReturnType<typeof resolveCanonicalCoachingFocus>>;
        try {
          proof = await ownedRequest(owner, () => resolveCanonicalCoachingFocus(input.activityId, {
            schema_version: 'canonical-focus-resolve.v1', player_id: input.playerId,
            ...(input.source.kind === 'feedback' ? { feedback_id: input.source.feedbackId } : { goal_id: input.source.goalId }),
          }, scope, read.abort.signal));
          if (!alive.current || !active.current || read.abort.signal.aborted || !read.boundary.current() || focusRead.current !== read)
            throw new Error('Focus context hidden or changed');
        } finally {
          if (focusRead.current === read) focusRead.current = null;
          read.boundary.dispose();
        }
        // A changed publication is a new choice, never silently substitute it.
        if (input.source.kind === 'feedback' && (proof.reference.source_kind !== 'feedback'
          || proof.reference.feedback_id !== input.source.feedbackId
          || proof.reference.feedback_revision_id !== input.source.revisionId
          || proof.reference.feedback_version !== input.source.version)) throw new Error('Selected Feedback version changed');
        if (input.source.kind === 'goal' && (proof.reference.source_kind !== 'goal'
          || proof.reference.goal_id !== input.source.goalId)) throw new Error('Selected Goal changed');
        focusAttempt.current = { input: structuredClone(input), command: captureCoachingManualCommand(owner, {
          action: 'select-focus', activityId: input.activityId, playerId: input.playerId,
          body: { schema_version: 'canonical-focus-select.v1', reference: proof.reference,
            expected_revision: input.expectedRevision, client_operation_id: input.operationId },
        }) };
      }
      return focusAttempt.current.command.send();
    }).finally(() => {
      if (preparedRead) {
        if (focusRead.current === preparedRead) focusRead.current = null;
        preparedRead.boundary.dispose();
      }
      if (focusDispatch.current?.promise === promise) focusDispatch.current = null;
    });
    focusDispatch.current = { input, promise };
    return promise;
  };
  const select = useMutation({ meta: { composerContext: owner }, retry: false,
    mutationFn: selectFocus,
    onSuccess: (_data, input) => { if (isCurrentDraftContext(owner)) {
      if (focusAttempt.current && JSON.stringify(focusAttempt.current.input) === JSON.stringify(input)) {
        focusAttempt.current.command.dispose(); focusAttempt.current = null;
      }
      if (alive.current && active.current) { void focuses.refetch(); invalidateScope(); }
    } },
  });
  // Freeze each previous request's keys for cleanup; never derive cleanup from
  // the new owner's stores. Cancelling queries prevents late private settlement.
  useEffect(() => () => {
    void client.cancelQueries({ queryKey: jobKey, exact: true });
    void client.cancelQueries({ queryKey: resultKey, exact: true });
    client.removeQueries({ queryKey: jobKey, exact: true });
    client.removeQueries({ queryKey: resultKey, exact: true });
  }, [client, JSON.stringify(jobKey), JSON.stringify(resultKey)]);
  useEffect(() => () => {
    for (const queryKey of [focusKey, choicesKey]) {
      void client.cancelQueries({ queryKey, exact: true }); client.removeQueries({ queryKey, exact: true });
    }
  }, [client, JSON.stringify(focusKey), JSON.stringify(choicesKey)]);
  useEffect(() => {
    if (!prepareVisible) for (const queryKey of [focusKey, choicesKey]) {
      void client.cancelQueries({ queryKey, exact: true }); client.removeQueries({ queryKey, exact: true });
    }
  }, [prepareVisible, client, JSON.stringify(focusKey), JSON.stringify(choicesKey)]);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    if (!prepareVisible) {
      void client.cancelQueries({ queryKey: jobKey, exact: true });
      void client.cancelQueries({ queryKey: resultKey, exact: true });
    }
  }, [prepareVisible, client, JSON.stringify(jobKey), JSON.stringify(resultKey)]);
  const operationError = admissionErrors[playerKey] ?? null;
  const generate = (task: 'player_preparation' | 'next_session_preparation') => {
    if (!prepareVisible || operation.isPending || operationError || isRunning(currentJob) || (currentJob && !currentJob.can_regenerate)) return;
    assertAssistOwner(owner);
    // Explicit new operation only. No automatic resubmission on any error.
    operation.reset();
    setJob(undefined);
    // Preparation uses the private result contract. Its target must also be
    // authorized by the dedicated text-only Assist policy.
    const request: CreateCoachingAIJobRequest = { client_operation_id: crypto.randomUUID(), evidence: {
      schema_version: 'coaching-evidence-task.v1', task, draft_target: 'feedback_message', audience: 'coach', publication_class: 'creator_private', selection_mode: 'automatic_history', player_ids: playerIds,
    } };
    admissionRequests.current.set(playerKey, request); operation.mutate(request);
  };
  const createContext = (input: Pick<CreateCoachingActivityRequest, 'kind' | 'match_id'>) => {
    if (newActivity.isPending) return;
    assertAssistOwner(owner);
    activityRequest.current ??= { ...input, occurred_at: new Date().toISOString(), client_operation_id: crypto.randomUUID() };
    newActivity.mutate(activityRequest.current);
  };
  const createActivityContext = () => createContext({ kind: 'training' });
  const createMatchContext = (matchId: string) => createContext({ kind: 'match', match_id: matchId });
  const reconcileAdmission = () => {
    const request = admissionRequests.current.get(playerKey);
    if (!visible || !request || operation.isPending || JSON.stringify(request.evidence.player_ids) !== playerKey) return;
    assertAssistOwner(owner); operation.mutate(request);
  };
  return { activities, activityId, setActivityId, focuses: { ...focuses, data: prepareVisible ? focuses.data : undefined },
    sourceChoices: { ...sourceChoices, data: prepareVisible ? sourceChoices.data : undefined },
    canReconcileFocus: !!focusAttempt.current,
    reconcileFocus: () => { if (focusAttempt.current && !select.isPending && active.current) select.mutate(focusAttempt.current.input); },
    canCorrectFocus: focusAttempt.current?.command.getState().status === 'rejected',
    correctFocus: () => { if (focusAttempt.current?.command.getState().status === 'rejected') {
      focusAttempt.current.command.dispose(); focusAttempt.current = null; select.reset();
    } }, newActivity, createActivityContext, createMatchContext, capture, select,
    generate, reconcileAdmission, canReconcile: !!operationError, operationError, operation, cancel, cancelPreparation: () => { if (job) cancel.mutate({ id: job.id, players: playerKey, jobKey, resultKey }); }, job: currentJob, progress, result };
}
